import {
	LocalizedTexts,
	MerchantSettings,
	MerchantToSet,
	ProductImage,
	SearchFilter,
	IntegrationSettings,
	MainImageVariant,
} from '@anticatrade/shared';
import { ProductToSet, ProductOrderBy } from '@anticatrade/shared';
import { Image } from '@anticatrade/shared';
import { call } from '@anticatrade/shared';
import { GET, POST, PUT, DELETE } from '@anticatrade/shared';
import {
	AddProductResponse,
	AuthorsResponse,
	SearchAuthorsResponse,
	CategoriesResponse,
	ConditionsResponse,
	CenturiesResponse,
	CredentialsResponse,
	ExchangeRatesResponse,
	LoginResponse,
	MaterialsResponse,
	MerchantResponse,
	MerchantSettingsResponse,
	ConversationsResponse,
	ConversationResponse,
	ProductResponse,
	ProductsResponse,
	StylesResponse,
	UnreadConversationsCountResponse,
	ViewsStatisticsResponse,
	ViewsTotalResponse,
	MessagesStatisticsResponse,
	MessagesTotalResponse,
	ViewsByCategoryResponse,
	SubscriptionUsageResponse,
	MerchantOrderInvoiceResponse,
	MerchantOrdersResponse,
	RemoveBackgroundResponse,
	CountriesResponse,
	OriginsResponse,
	FollowersTotalResponse,
	FavoritesTotalResponse,
	ProductDraftsResponse,
	ProductDraftResponse,
	CreateProductDraftResponse,
	ImportProductResponse,
	IntegrationsResponse,
	MerchantIntegrationsResponse,
	AddMerchantIntegrationResponse,
	AddPublicationResponse,
	PublicationLimitResponse,
	MerchantIntegrationTaxonomyResponse,
	PublishToWebsiteResponse,
} from './types';

class API {
	apiURL: string;
	language: string;

	constructor(serverURL: string, lang: string) {
		this.apiURL = `${serverURL}/api/admin`;
		this.language = lang;
	}

	_call = async (
		url: string,
		method: string,
		body?: any,
		formData?: FormData | null,
		opt?: any,
	) => {
		return await call(url, this.language, method, body, formData, opt);
	};

	products = async (
		page: number,
		limit: number,
		ids?: number[],
		filter?: SearchFilter,
		orderBy?: ProductOrderBy | null,
		opt?: any,
	): Promise<ProductsResponse> => {
		return await this._call(
			`${this.apiURL}/products/search`,
			POST,
			{
				page,
				limit,
				ids,
				filter,
				orderBy,
			},
			null,
			opt,
		);
	};

	product = async (product: number, opt?: any): Promise<ProductResponse> => {
		return await this._call(
			`${this.apiURL}/products/${encodeURIComponent(product)}`,
			GET,
			null,
			null,
			opt,
		);
	};

	addProduct = async (
		productToAdd: ProductToSet,
		image: Image,
		imageOriginal: Image | undefined,
		gallery: Image[],
		opt?: any,
	): Promise<AddProductResponse> => {
		const form = new FormData();
		form.append('productToAdd', JSON.stringify(productToAdd));
		form.append('image[file]', image.file!);
		if (imageOriginal?.file != null) {
			form.append('image_original[file]', imageOriginal.file);
		}
		let counter = 0;
		for (const img of gallery) {
			form.append(`gallery[${counter}][file]`, img.file!);
			form.append(`gallery[${counter}][position]`, String(img.position));
			counter++;
		}
		return await this._call(`${this.apiURL}/products`, POST, null, form, opt);
	};

	updateProduct = async (
		product: number,
		productToSet: ProductToSet,
		image: Image,
		imageOriginal: Image | undefined,
		gallery: Image[],
		originalGallery: ProductImage[],
		opt?: any,
	): Promise<void> => {
		const form = new FormData();
		form.append('productToSet', JSON.stringify(productToSet));

		if (image.file != null) {
			form.append('image[file]', image.file);
		}

		if (imageOriginal?.file != null) {
			form.append('image_original[file]', imageOriginal.file);
		}

		const removed = originalGallery.filter((orig) => !gallery.some((g) => g.id === orig.id));
		const updatedPositions = gallery
			.filter((g) => g.id != null)
			.filter((g) => {
				const orig = originalGallery.find((o) => o.id === g.id);
				return orig != null && orig.position !== g.position;
			});
		const added = gallery.filter((g) => g.file != null);

		form.append('gallery_removed', JSON.stringify(removed.map((r) => r.id)));
		form.append(
			'gallery_updated_positions',
			JSON.stringify(updatedPositions.map((u) => ({ id: u.id, position: u.position }))),
		);

		let counter = 0;
		for (const img of added) {
			form.append(`gallery_added[${counter}][file]`, img.file!);
			form.append(`gallery_added[${counter}][position]`, String(img.position));
			counter++;
		}

		return await this._call(
			`${this.apiURL}/products/${encodeURIComponent(product)}`,
			PUT,
			null,
			form,
			opt,
		);
	};

	updateProductTexts = async (
		product: number,
		title: LocalizedTexts,
		description: LocalizedTexts,
		opt?: any,
	): Promise<void> => {
		return await this._call(
			`${this.apiURL}/products/${encodeURIComponent(product)}/texts`,
			PUT,
			{ title, description },
			null,
			opt,
		);
	};

	deleteProduct = async (product: number, opt?: any): Promise<void> => {
		return await this._call(
			`${this.apiURL}/products/${encodeURIComponent(product)}`,
			DELETE,
			product,
			null,
			opt,
		);
	};

	productDrafts = async (opt?: any): Promise<ProductDraftsResponse> => {
		return await this._call(`${this.apiURL}/drafts`, GET, null, null, opt);
	};

	productDraft = async (id: number, opt?: any): Promise<ProductDraftResponse> => {
		return await this._call(
			`${this.apiURL}/drafts/${encodeURIComponent(id)}`,
			GET,
			null,
			null,
			opt,
		);
	};

	createProductDraft = async (
		formData: string,
		image?: File | null,
		originalImage?: File | null,
		galleryFiles?: File[],
		opt?: any,
	): Promise<CreateProductDraftResponse> => {
		const form = new FormData();
		form.append('formData', formData);
		if (image) {
			form.append('image[file]', image);
		}
		if (originalImage) {
			form.append('originalImage[file]', originalImage);
		}
		if (galleryFiles) {
			let counter = 0;
			for (const file of galleryFiles) {
				form.append(`gallery[${counter}][file]`, file);
				counter++;
			}
		}
		return await this._call(`${this.apiURL}/drafts`, POST, null, form, opt);
	};

	updateProductDraft = async (
		id: number,
		formData: string,
		image?: File | null,
		originalImage?: File | null,
		galleryFiles?: File[],
		opt?: any,
	): Promise<void> => {
		const form = new FormData();
		form.append('formData', formData);
		if (image) {
			form.append('image[file]', image);
		}
		if (originalImage) {
			form.append('originalImage[file]', originalImage);
		}
		if (galleryFiles) {
			let counter = 0;
			for (const file of galleryFiles) {
				form.append(`gallery[${counter}][file]`, file);
				counter++;
			}
		}
		return await this._call(
			`${this.apiURL}/drafts/${encodeURIComponent(id)}`,
			PUT,
			null,
			form,
			opt,
		);
	};

	deleteProductDraft = async (id: number, opt?: any): Promise<void> => {
		return await this._call(
			`${this.apiURL}/drafts/${encodeURIComponent(id)}`,
			DELETE,
			null,
			null,
			opt,
		);
	};

	removeBackground = async (image: File, opt?: any): Promise<RemoveBackgroundResponse> => {
		const form = new FormData();
		form.append('image', image);
		return await this._call(`${this.apiURL}/remove-background`, POST, null, form, opt);
	};

	merchant = async (opt?: any): Promise<MerchantResponse> => {
		return await this._call(`${this.apiURL}/merchant`, GET, null, null, opt);
	};

	updateMerchant = async (
		merchantToSet: MerchantToSet,
		logo: Image | undefined,
		cover: Image | undefined,
		removeCover?: boolean,
		opt?: any,
	): Promise<void> => {
		const form = new FormData();
		form.append('merchantToSet', JSON.stringify(merchantToSet));
		if (logo != null && logo.file != null) {
			form.append('logo[file]', logo.file);
		}
		if (cover != null && cover.file != null) {
			form.append('cover[file]', cover.file);
		}
		if (removeCover) {
			form.append('removeCover', 'true');
		}
		return await this._call(`${this.apiURL}/merchant`, PUT, null, form, opt);
	};

	merchantSettings = async (opt?: any): Promise<MerchantSettingsResponse> => {
		return await this._call(`${this.apiURL}/merchant/settings`, GET, null, null, opt);
	};

	updateMerchantSettings = async (settings: MerchantSettings, opt?: any): Promise<void> => {
		return await this._call(`${this.apiURL}/merchant/settings`, PUT, { settings }, null, opt);
	};

	subscriptionUsage = async (opt?: any): Promise<SubscriptionUsageResponse> => {
		return await this._call(`${this.apiURL}/merchant/subscription-usage`, GET, null, null, opt);
	};

	merchantOrders = async (opt?: any): Promise<MerchantOrdersResponse> => {
		return await this._call(`${this.apiURL}/merchant-orders`, GET, null, null, opt);
	};

	conversations = async (
		page: number,
		limit: number,
		opt?: any,
	): Promise<ConversationsResponse> => {
		return await this._call(`${this.apiURL}/conversations`, POST, { page, limit }, null, opt);
	};

	conversation = async (id: number, opt?: any): Promise<ConversationResponse> => {
		return await this._call(
			`${this.apiURL}/conversations/${encodeURIComponent(id)}`,
			GET,
			null,
			null,
			opt,
		);
	};

	markConversationAsRead = async (id: number, opt?: any): Promise<void> => {
		return await this._call(
			`${this.apiURL}/conversations/${encodeURIComponent(id)}/read`,
			POST,
			null,
			null,
			opt,
		);
	};

	unreadConversationsCount = async (opt?: any): Promise<UnreadConversationsCountResponse> => {
		return await this._call(`${this.apiURL}/conversations/unread-count`, GET, null, null, opt);
	};

	conversationAttachmentURL = (attachmentId: number): string => {
		return `${this.apiURL}/conversations/attachments/${encodeURIComponent(attachmentId)}`;
	};

	viewsStatistics = async (opt?: any): Promise<ViewsStatisticsResponse> => {
		return await this._call(`${this.apiURL}/statistics/views`, GET, null, null, opt);
	};

	viewsTotal = async (opt?: any): Promise<ViewsTotalResponse> => {
		return await this._call(`${this.apiURL}/statistics/views-total`, GET, null, null, opt);
	};

	viewsByCategory = async (opt?: any): Promise<ViewsByCategoryResponse> => {
		return await this._call(`${this.apiURL}/statistics/views-category`, GET, null, null, opt);
	};

	messagesStatistics = async (opt?: any): Promise<MessagesStatisticsResponse> => {
		return await this._call(`${this.apiURL}/statistics/messages`, GET, null, null, opt);
	};

	messagesTotal = async (opt?: any): Promise<MessagesTotalResponse> => {
		return await this._call(`${this.apiURL}/statistics/messages-total`, GET, null, null, opt);
	};

	followersTotal = async (opt?: any): Promise<FollowersTotalResponse> => {
		return await this._call(`${this.apiURL}/statistics/followers-total`, GET, null, null, opt);
	};

	favoritesTotal = async (opt?: any): Promise<FavoritesTotalResponse> => {
		return await this._call(`${this.apiURL}/statistics/favorites-total`, GET, null, null, opt);
	};

	merchantOrderInvoice = async (
		merchantOrder: number,
		opt?: any,
	): Promise<MerchantOrderInvoiceResponse> => {
		return await this._call(
			`${this.apiURL}/merchant-orders/${encodeURIComponent(merchantOrder)}/invoice`,
			GET,
			null,
			null,
			opt,
		);
	};

	categories = async (opt?: any): Promise<CategoriesResponse> => {
		return await this._call(`${this.apiURL}/categories`, GET, null, null, opt);
	};

	conditions = async (opt?: any): Promise<ConditionsResponse> => {
		return await this._call(`${this.apiURL}/conditions`, GET, null, null, opt);
	};

	centuries = async (opt?: any): Promise<CenturiesResponse> => {
		return await this._call(`${this.apiURL}/centuries`, GET, null, null, opt);
	};

	styles = async (opt?: any): Promise<StylesResponse> => {
		return await this._call(`${this.apiURL}/styles`, GET, null, null, opt);
	};

	origins = async (opt?: any): Promise<OriginsResponse> => {
		return await this._call(`${this.apiURL}/origins`, GET, null, null, opt);
	};

	authors = async (opt?: any): Promise<AuthorsResponse> => {
		return await this._call(`${this.apiURL}/authors`, GET, null, null, opt);
	};

	searchAuthors = async (
		page: number,
		limit: number,
		search: string,
		opt?: any,
	): Promise<SearchAuthorsResponse> => {
		return await this._call(
			`${this.apiURL}/authors/search`,
			POST,
			{ page, limit, search },
			null,
			opt,
		);
	};

	countries = async (opt?: any): Promise<CountriesResponse> => {
		return await this._call(`${this.apiURL}/countries`, GET, null, null, opt);
	};

	materials = async (opt?: any): Promise<MaterialsResponse> => {
		return await this._call(`${this.apiURL}/materials`, GET, null, null, opt);
	};

	exchangeRates = async (opt?: any): Promise<ExchangeRatesResponse> => {
		return await this._call(`${this.apiURL}/exchange-rates`, GET, null, null, opt);
	};

	credentials = async (opt?: any): Promise<CredentialsResponse> => {
		return await this._call(`${this.apiURL}/users/credentials`, GET, null, null, opt);
	};

	login = async (
		email: string,
		password: string,
		captchaToken: string,
		opt?: any,
	): Promise<LoginResponse> => {
		return await this._call(
			`${this.apiURL}/users/login`,
			POST,
			{ email, password, captchaToken },
			null,
			opt,
		);
	};

	logout = async (opt?: any): Promise<void> => {
		return await this._call(`${this.apiURL}/users/logout`, POST, null, null, opt);
	};

	importProduct = async (
		url: string,
		platform: string,
		opt?: any,
	): Promise<ImportProductResponse> => {
		return await this._call(
			`${this.apiURL}/import-product`,
			POST,
			{ url, platform },
			null,
			opt,
		);
	};

	integrations = async (opt?: any): Promise<IntegrationsResponse> => {
		return await this._call(`${this.apiURL}/integrations`, GET, null, null, opt);
	};

	merchantIntegrations = async (opt?: any): Promise<MerchantIntegrationsResponse> => {
		return await this._call(`${this.apiURL}/merchant/integrations`, GET, null, null, opt);
	};

	addMerchantIntegration = async (
		integrationKey: string,
		settings: IntegrationSettings,
		opt?: any,
	): Promise<AddMerchantIntegrationResponse> => {
		return await this._call(
			`${this.apiURL}/merchant/integrations`,
			POST,
			{ integrationKey, settings },
			null,
			opt,
		);
	};

	updateMerchantIntegration = async (
		id: number,
		settings: IntegrationSettings,
		opt?: any,
	): Promise<void> => {
		return await this._call(
			`${this.apiURL}/merchant/integrations/${id}`,
			PUT,
			{ settings },
			null,
			opt,
		);
	};

	deleteMerchantIntegration = async (id: number, opt?: any): Promise<void> => {
		return await this._call(
			`${this.apiURL}/merchant/integrations/${id}`,
			DELETE,
			null,
			null,
			opt,
		);
	};

	publicationLimit = async (
		merchantIntegrationID: number,
		opt?: any,
	): Promise<PublicationLimitResponse> => {
		return await this._call(
			`${this.apiURL}/merchant/integrations/${merchantIntegrationID}/publication-limit`,
			GET,
			null,
			null,
			opt,
		);
	};

	addPublication = async (
		merchantIntegrationID: number,
		productID: number,
		externalID?: string | null,
		opt?: any,
	): Promise<AddPublicationResponse> => {
		return await this._call(
			`${this.apiURL}/merchant/integrations/${merchantIntegrationID}/products/${productID}/publication`,
			POST,
			{ externalID: externalID ?? null },
			null,
			opt,
		);
	};

	removePublication = async (
		merchantIntegrationID: number,
		productID: number,
		opt?: any,
	): Promise<void> => {
		return await this._call(
			`${this.apiURL}/merchant/integrations/${merchantIntegrationID}/products/${productID}/publication`,
			DELETE,
			null,
			null,
			opt,
		);
	};

	merchantIntegrationTaxonomy = async (
		merchantIntegrationID: number,
		opt?: any,
	): Promise<MerchantIntegrationTaxonomyResponse> => {
		return await this._call(
			`${this.apiURL}/merchant/integrations/${merchantIntegrationID}/taxonomy`,
			GET,
			null,
			null,
			opt,
		);
	};

	publishToWebsite = async (
		merchantIntegrationID: number,
		productID: number,
		product: Record<string, unknown>,
		mainImageVariant: MainImageVariant,
		opt?: any,
	): Promise<PublishToWebsiteResponse> => {
		return await this._call(
			`${this.apiURL}/merchant/integrations/${merchantIntegrationID}/products/${productID}/publish`,
			POST,
			{ product, mainImageVariant },
			null,
			opt,
		);
	};
}

export { API };
